import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  BuildingWithConvertedAddresses,
  OffMarketWillingnessEvent,
} from '../utils/types';
import {
  AreaDemandCard,
  getAreaInterestsForBuilding,
} from '../AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import { getBuildingListings } from '../utils/buildingListings';
import {
  getBuildingLikeCoordinates,
  withLikeRequestCoordinates,
} from '../utils/buildingLike';
import { getCookie } from '../utils/utils';

export interface BuildingPreviewMeta {
  buyers: number;
  likes: number;
  followers: number;
  liked: boolean;
}

export type BuildingPreviewMetaPatch = Partial<
  Pick<BuildingPreviewMeta, 'likes' | 'followers' | 'liked'>
>;

export interface BuildingPreviewState {
  building: BuildingWithConvertedAddresses | null;
  anchor: { x: number; y: number } | null;
  listings: OffMarketWillingnessEvent[];
  /** Anonymized area interests whose footprint covers this building. */
  areaDemand: AreaDemandCard[];
  loading: boolean;
  meta: BuildingPreviewMeta;
}

/** Already-loaded data to paint the preview instantly (stale-while-revalidate).
 *  The area's first load has the building's listings (in `results`) and its
 *  buyer/like counts (in the aggregated map features), so we render those at
 *  once and only hit the network to fill the user-specific fields the map
 *  payload lacks (followers, `liked`). */
export interface BuildingPreviewSeed {
  listings: OffMarketWillingnessEvent[];
  meta: BuildingPreviewMeta;
}

export interface BuildingPreviewService extends BuildingPreviewState {
  /** Open the preview for a building. `anchor` = the on-screen point to anchor
   *  the popover to on desktop (map click point or projected building point);
   *  omit for the docked fallback. `seed` = in-memory data to paint instantly
   *  (no spinner); the network fetch then refreshes in the background. */
  open: (
    building: BuildingWithConvertedAddresses,
    anchor?: { x: number; y: number },
    seed?: BuildingPreviewSeed,
  ) => void;
  /** Open the preview for the map selection that is still current, or swap in
   *  the enriched object when that building is already open. Unlike `open` it
   *  does not advance `selectionRef`, so the same click's later geocode
   *  callbacks still apply. */
  openFromMap: (
    building: BuildingWithConvertedAddresses,
    anchor?: { x: number; y: number },
    seed?: BuildingPreviewSeed,
  ) => void;
  /** The one active selection token. `open`, `close` and unmount advance it;
   *  a map sharing it advances it on every click and on its own unmount, and
   *  hands the click's value back with each delayed geocode callback, which
   *  may act only while that value is still current. */
  selectionRef: MutableRefObject<number>;
  /** Swap in an enriched building object (address/POIs resolved) for the
   *  already-open preview, without resetting listings/meta/anchor or re-fetching.
   *  Used to upgrade the instant bare-marker open once geocoding completes. */
  update: (building: BuildingWithConvertedAddresses) => void;
  updateMeta: (buildingId: string, patch: BuildingPreviewMetaPatch) => void;
  /** Refetch listings + counts for the currently open preview. If a building id
   *  is provided, no-op unless it matches the active preview. */
  refresh: (buildingId?: string) => void;
  close: () => void;
  toggleLike: (onLiked?: (building: BuildingWithConvertedAddresses) => void) => void;
}

const EMPTY_META: BuildingPreviewMeta = { buyers: 0, likes: 0, followers: 0, liked: false };

export const countBuildingBuyers = (
  listings: OffMarketWillingnessEvent[],
): number => listings.filter((listing) => listing.could_buy).length;

/**
 * Shared on-map building-preview service. A single source of truth for the
 * "active preview" so the explore list AND the map markers drive the same
 * BuildingPreviewCard component. Owns the building's listings + counts fetch.
 */
export const useBuildingPreview = (): BuildingPreviewService => {
  const [building, setBuilding] = useState<BuildingWithConvertedAddresses | null>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const [listings, setListings] = useState<OffMarketWillingnessEvent[]>([]);
  const [areaDemand, setAreaDemand] = useState<AreaDemandCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<BuildingPreviewMeta>(EMPTY_META);
  const activeBuildingIdRef = useRef<string | null>(null);
  const activeBuildingRef = useRef<BuildingWithConvertedAddresses | null>(null);
  const openAbortControllerRef = useRef<AbortController | null>(null);
  const selectionRef = useRef(0);

  useEffect(() => {
    const selection = selectionRef;
    return () => {
      selection.current += 1;
    };
  }, []);

  const loadPreviewData = useCallback(
    (
      target: BuildingWithConvertedAddresses,
      controller: AbortController,
      keepSeedOnEmpty: boolean,
    ) => {
      const targetBuildingId = String(target.id);
      const isCurrentBuilding = () =>
        activeBuildingIdRef.current === targetBuildingId &&
        openAbortControllerRef.current === controller &&
        !controller.signal.aborted;

      const headers = {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
      };

      // The empty-state prompt renders only once BOTH the building's listings and
      // the covering area interests are known, so the loading flag must
      // out-live whichever of the two fetches finishes last — clearing it on
      // listings alone flashed the prompt for buildings whose only demand is
      // area-level. The meta fetch stays out of the count: its counts refine
      // an already-painted card and never gate the empty state.
      let pendingLoads = 2;
      const settleLoad = () => {
        pendingLoads -= 1;
        if (pendingLoads === 0 && isCurrentBuilding()) setLoading(false);
      };

      getBuildingListings(target.id, { signal: controller.signal })
        .then((rows) => {
          // null is a dropped request: leave the current/seeded listings
          // untouched rather than blanking the preview, as the old catch did.
          if (!isCurrentBuilding() || !rows) return;
          const off = rows as unknown as OffMarketWillingnessEvent[];
          // Only a successful response is authoritative. Seeded opens are
          // stale-while-revalidate, so an empty result keeps the seed; an
          // authoritative refresh may legitimately replace listings with [].
          if (off.length > 0 || !keepSeedOnEmpty) {
            setListings(off);
            // Buyer demand is owned by the willingness rows. The legacy
            // buildings counter may be stale or the building document may not
            // exist at all for a buy-only building.
            setMeta((current) => ({
              ...current,
              buyers: countBuildingBuyers(off),
            }));
          }
        })
        .finally(settleLoad);

      // Area interests covering this building (wider buyer demand). The map
      // payload has no seed for these, so the preview shows them once this
      // lands. The map's own point rides along as the backend's fallback: this
      // surface opens on buildings the database has never stored, and those
      // cannot be placed from an id alone (JAA-347).
      getAreaInterestsForBuilding(targetBuildingId, {
        coords: getBuildingLikeCoordinates(target),
        signal: controller.signal,
      })
        .then((rows) => {
          if (!isCurrentBuilding()) return;
          // A successful response is authoritative either way — an empty list
          // legitimately means no area covers this building — while null is a
          // dropped request, where the row keeps whatever is showing rather than
          // blanking, mirroring the listings fetch above.
          if (rows) setAreaDemand(rows);
        })
        .finally(settleLoad);

      fetch(`${process.env.REACT_APP_API_URL}/building?id=${target.id}`, {
        credentials: 'include',
        headers,
        signal: controller.signal,
      })
        .then((res) => res.json())
        .then((data) => {
          if (!isCurrentBuilding()) return;
          const p = data?.properties || {};
          setMeta((current) => ({
            ...current,
            likes: p.like_count || 0,
            followers: p.subscribersCount || 0,
            liked: p.user_has_liked || false,
          }));
        })
        .catch(() => {});
    },
    [],
  );

  /** Shows `next` in the preview and starts its listings/counts fetch. */
  const show = useCallback(
    (
      next: BuildingWithConvertedAddresses,
      nextAnchor?: { x: number; y: number },
      seed?: BuildingPreviewSeed,
    ) => {
      const nextBuildingId = String(next.id);
      activeBuildingIdRef.current = nextBuildingId;
      activeBuildingRef.current = next;
      openAbortControllerRef.current?.abort();
      const controller = new AbortController();
      openAbortControllerRef.current = controller;

      setBuilding(next);
      setAnchor(nextAnchor ?? null);

      // Seeded → paint instantly from in-memory data, no spinner. The fetches
      // below still run to refresh (authoritative listings + followers/liked).
      const hasSeed = !!seed && seed.listings.length > 0;
      // No seed exists for area demand (the map payload doesn't carry it), so
      // always clear the previous building's rows until the fetch lands.
      setAreaDemand([]);
      if (hasSeed) {
        setListings(seed!.listings);
        setMeta(seed!.meta);
        setLoading(false);
      } else {
        setListings([]);
        setLoading(true);
        setMeta(EMPTY_META);
      }

      loadPreviewData(next, controller, hasSeed);
    },
    [loadPreviewData],
  );

  /** Opens a preview from outside the map selection flow, superseding any pending map selection. */
  const open = useCallback(
    (
      next: BuildingWithConvertedAddresses,
      nextAnchor?: { x: number; y: number },
      seed?: BuildingPreviewSeed,
    ) => {
      selectionRef.current += 1;
      show(next, nextAnchor, seed);
    },
    [show],
  );

  const update = useCallback((next: BuildingWithConvertedAddresses) => {
    // Clone — the map mutates the building object in place, so `next` may be the
    // same reference already in state; a fresh object guarantees a re-render.
    setBuilding((current) => {
      if (!current || current.id !== next.id) return current;
      const updated = { ...next };
      activeBuildingRef.current = updated;
      return updated;
    });
  }, []);

  /** Opens or enriches the preview for the current map selection without advancing the token. */
  const openFromMap = useCallback(
    (
      next: BuildingWithConvertedAddresses,
      nextAnchor?: { x: number; y: number },
      seed?: BuildingPreviewSeed,
    ) => {
      if (activeBuildingIdRef.current === String(next.id)) {
        update(next);
        return;
      }
      show(next, nextAnchor, seed);
    },
    [show, update],
  );

  const updateMeta = useCallback(
    (buildingId: string, patch: BuildingPreviewMetaPatch) => {
      setBuilding((current) => {
        if (!current || current.id !== buildingId) return current;
        setMeta((m) => ({
          ...m,
          ...(typeof patch.likes === 'number'
            ? { likes: Math.max(0, patch.likes) }
            : {}),
          ...(typeof patch.followers === 'number'
            ? { followers: Math.max(0, patch.followers) }
            : {}),
          ...(typeof patch.liked === 'boolean' ? { liked: patch.liked } : {}),
        }));
        return current;
      });
    },
    [],
  );

  const refresh = useCallback(
    (buildingId?: string) => {
      const current = activeBuildingRef.current;
      const activeBuildingId = activeBuildingIdRef.current;
      if (!current || !activeBuildingId) return;
      if (buildingId && String(buildingId) !== activeBuildingId) return;

      openAbortControllerRef.current?.abort();
      const controller = new AbortController();
      openAbortControllerRef.current = controller;

      loadPreviewData(current, controller, false);
    },
    [loadPreviewData],
  );

  /** Closes the preview and supersedes any pending map selection. */
  const close = useCallback(() => {
    selectionRef.current += 1;
    activeBuildingIdRef.current = null;
    activeBuildingRef.current = null;
    openAbortControllerRef.current?.abort();
    openAbortControllerRef.current = null;
    setBuilding(null);
    setAnchor(null);
    setListings([]);
    setAreaDemand([]);
  }, []);

  const toggleLike = useCallback((onLiked?: (building: BuildingWithConvertedAddresses) => void) => {
    setBuilding((current) => {
      if (!current) return current;
      setMeta((m) => {
        const previousLiked = m.liked;
        const previousLikes = m.likes;
        const liked = !m.liked;
        const requestBuilding = withLikeRequestCoordinates(current);
        fetch(`${process.env.REACT_APP_API_URL}/like-building`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
          },
          body: JSON.stringify({ like: liked, selected_building: requestBuilding }),
        })
          .then((res) => {
            if (!res.ok) throw new Error('Failed to toggle building like');
            return res.json();
          })
          .then((data: { like_count: number; user_has_liked: boolean }) => {
            setMeta((latest) => ({
              ...latest,
              liked: data.user_has_liked,
              likes: Math.max(0, data.like_count),
            }));
            window.dispatchEvent(new CustomEvent('jaakko:listings-changed'));
            if (liked && data.user_has_liked) {
              onLiked?.(requestBuilding);
            }
          })
          .catch(() => {
            setMeta((latest) => ({
              ...latest,
              liked: previousLiked,
              likes: previousLikes,
            }));
          });
        return { ...m, liked, likes: Math.max(0, m.likes + (liked ? 1 : -1)) };
      });
      return current;
    });
  }, []);

  return {
    building,
    anchor,
    listings,
    areaDemand,
    loading,
    meta,
    open,
    openFromMap,
    selectionRef,
    update,
    updateMeta,
    refresh,
    close,
    toggleLike,
  };
};

export default useBuildingPreview;
